import React, { useCallback, useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import { ClipboardList, Lock, Plus } from 'lucide-react';
import ReadOnlyBanner from '../ReadOnlyBanner.tsx';
import { AppDataTable } from '../../shared/table/AppDataTable.tsx';
import type { TableColumnDef } from '../../shared/table/types.ts';
import { formatDate } from '../analytics/controller/shared.tsx';
import { usePBCControlOptions, usePBCRequests, useUpdatePBCStatus } from '../../hooks/pbc/usePBCRequests.ts';
import { PBC_STATUSES, type PBCRequest, type PBCStatus } from '../../request/pbc/pbcRequests.ts';
import { CreatePBCRequestModal } from './CreatePBCRequestModal.tsx';
import { useAuditYear } from '../../contexts/AuditYearContext.tsx';

const STATUS_FILTER_OPTIONS: Array<{ value: '' | PBCStatus; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'received', label: 'Received' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'overdue', label: 'Overdue' },
];

const PBC_STATUS_CLASSES: Record<PBCStatus, string> = {
  pending: 'bg-amber-100 text-amber-700',
  received: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  overdue: 'bg-red-100 text-red-700',
};

const PBCStatusChip: React.FC<{ status: PBCStatus }> = ({ status }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${
      PBC_STATUS_CLASSES[status] ?? 'bg-gray-100 text-gray-700'
    }`}
  >
    {status}
  </span>
);

function is403(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 403;
}

/**
 * PBC (Provided-by-Client) request tracker — Controller report #29
 * (Feature 5.3). Unlike the read-only analytics dashboards, this is a full
 * CRUD surface: list + create + per-row status change, backed by
 * `/api/pbc` (writes gated server-side by `RequireOrgAdminForWrites()`).
 */
export const PBCTracker: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<'' | PBCStatus>('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [writesForbidden, setWritesForbidden] = useState(false);

  const { selectedAuditYear, selectedAuditYearId } = useAuditYear();
  const isLocked = Boolean(selectedAuditYear?.is_locked);

  const pbcParams = {
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(selectedAuditYearId ? { audit_year_id: selectedAuditYearId } : {}),
  };
  const { data, isLoading, isError, error } = usePBCRequests(pbcParams);
  const { data: controlOptions } = usePBCControlOptions();
  const updateStatusMutation = useUpdatePBCStatus();

  const controlLabelById = useMemo(() => {
    const map = new Map<string, string>();
    (controlOptions ?? []).forEach((opt) => {
      map.set(opt.id, opt.control_id ? `${opt.control_id} — ${opt.title}` : opt.title || opt.id);
    });
    return map;
  }, [controlOptions]);

  const handleStatusChange = useCallback(
    (row: PBCRequest, nextStatus: PBCStatus) => {
      if (nextStatus === row.status) return;
      updateStatusMutation.mutate(
        { id: row.id, status: nextStatus },
        {
          onError: (err) => {
            if (is403(err)) setWritesForbidden(true);
          },
        }
      );
    },
    [updateStatusMutation]
  );

  const handleCreateClick = () => {
    if (writesForbidden || isLocked) return;
    setIsCreateOpen(true);
  };

  const columns = useMemo<TableColumnDef<PBCRequest>[]>(
    () => [
      {
        id: 'control',
        columnLabel: 'Control',
        header: () => 'Control',
        cell: ({ row }) => (
          <span className="font-medium text-gray-900" title={row.original.sox_control_id}>
            {controlLabelById.get(row.original.sox_control_id) ?? row.original.sox_control_id}
          </span>
        ),
        widthPercent: 18,
      },
      {
        id: 'title',
        columnLabel: 'PBC Type / Title',
        header: () => 'PBC Type / Title',
        cell: ({ row }) => row.original.title,
        widthPercent: 20,
        truncate: true,
      },
      {
        id: 'due_date',
        columnLabel: 'Due',
        header: () => 'Due',
        cell: ({ row }) => formatDate(row.original.due_date),
        widthPercent: 8,
      },
      {
        id: 'testing_period',
        columnLabel: 'Testing Period',
        header: () => 'Testing Period',
        cell: ({ row }) =>
          row.original.testing_period_start || row.original.testing_period_end
            ? `${formatDate(row.original.testing_period_start)} – ${formatDate(row.original.testing_period_end)}`
            : '—',
        widthPercent: 14,
      },
      {
        id: 'status',
        columnLabel: 'Status',
        header: () => 'Status',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <PBCStatusChip status={row.original.status} />
            <select
              aria-label={`Change status for ${row.original.title}`}
              value={row.original.status}
              disabled={writesForbidden || isLocked || updateStatusMutation.isPending}
              onChange={(e) => handleStatusChange(row.original, e.target.value as PBCStatus)}
              className="rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs text-gray-700 disabled:opacity-50"
            >
              {PBC_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        ),
        widthPercent: 18,
      },
      {
        id: 'assigned_to_user_id',
        columnLabel: 'Assignee',
        header: () => 'Assignee',
        cell: ({ row }) =>
          row.original.assigned_to_name ? (
            row.original.assigned_to_name
          ) : row.original.assigned_to_user_id != null ? (
            `User #${row.original.assigned_to_user_id}`
          ) : (
            <span className="text-gray-400">Unassigned</span>
          ),
        widthPercent: 11,
      },
      {
        id: 'created_at',
        columnLabel: 'Requested',
        header: () => 'Requested',
        cell: ({ row }) => formatDate(row.original.created_at),
        widthPercent: 12,
      },
    ],
    [controlLabelById, writesForbidden, isLocked, updateStatusMutation.isPending, handleStatusChange]
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-blue-100 text-blue-600">
              <ClipboardList className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">PBC Tracker</h1>
              <p className="text-sm text-gray-500">
                Provided-by-client evidence requests, status, and due dates by control
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleCreateClick}
            disabled={writesForbidden || isLocked}
            title={
              isLocked
                ? `${selectedAuditYear?.name ?? 'This year'} is locked — new PBC requests cannot be created`
                : writesForbidden
                  ? 'You do not have permission to create PBC requests'
                  : undefined
            }
            className="inline-flex items-center gap-2 rounded-md border border-blue-600 bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:border-gray-300"
          >
            <Plus className="h-4 w-4" />
            New PBC Request
          </button>
        </div>

        <ReadOnlyBanner />

        {writesForbidden ? (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <Lock className="h-4 w-4 shrink-0" />
            <span>
              Your role does not have permission to create PBC requests or change their status. You can still view
              the tracker below.
            </span>
          </div>
        ) : null}

        <div className="mb-4 flex items-center gap-2">
          <label htmlFor="pbc-status-filter" className="text-sm font-medium text-gray-700">
            Status
          </label>
          <select
            id="pbc-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as '' | PBCStatus)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <AppDataTable<PBCRequest>
          data={data ?? []}
          columns={columns}
          loading={isLoading}
          error={isError ? error?.message ?? 'Failed to load PBC requests.' : null}
          emptyMessage="No PBC requests yet."
          searchable
          searchPlaceholder="Search PBC requests..."
          paginated
          defaultPageSize={10}
          showPageSizeSelector
        />
      </div>

      <CreatePBCRequestModal
        isOpen={isCreateOpen && !writesForbidden && !isLocked}
        onClose={() => setIsCreateOpen(false)}
        onForbidden={() => setWritesForbidden(true)}
      />
    </div>
  );
};

export default PBCTracker;
